import React from "react";
import { Link, Outlet } from "react-router-dom";
import OnboardingStepper from "./OnboardingStepper";

type OnboardingShellProps = {
  title?: string;
  description?: string;
  children?: React.ReactNode;
};

const shellBackground = "#f8fafc";
const panelBackground = "#ffffff";
const borderColor = "#e5e7eb";
const mutedText = "#64748b";
const bodyText = "#334155";
const headingText = "#0f172a";

const defaultTitle = "Set up your business on HnB";
const defaultDescription =
  "Complete each step to configure your provider profile, verification, services, availability, payment rules, and portfolio before go-live.";

const quickLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  background: "#ffffff",
  color: "#111827",
  padding: "0.7rem 0.95rem",
  fontWeight: 700,
  fontSize: "0.92rem",
};

const OnboardingShell: React.FC<OnboardingShellProps> = ({
  title,
  description,
  children,
}) => {
  const content = children ?? <Outlet />;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: shellBackground,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "2rem 1.25rem 4rem",
        }}
      >
        <div
          style={{
            marginBottom: "1.5rem",
          }}
        >
          <p
            style={{
              color: mutedText,
              marginBottom: "0.5rem",
              fontSize: "0.92rem",
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            Provider onboarding
          </p>

          <h1
            style={{
              margin: 0,
              marginBottom: "0.75rem",
              color: headingText,
              fontSize: "2rem",
              lineHeight: 1.15,
              fontWeight: 800,
            }}
          >
            {title ?? defaultTitle}
          </h1>

          <p
            style={{
              color: bodyText,
              maxWidth: 780,
              margin: 0,
              fontSize: "1rem",
              lineHeight: 1.7,
            }}
          >
            {description ?? defaultDescription}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap",
            marginBottom: "1rem",
          }}
        >
          <Link to="/provider/dashboard" style={quickLinkStyle}>
            Provider dashboard
          </Link>
          <Link to="/provider/dashboard/stylists" style={quickLinkStyle}>
            Stylists
          </Link>
          <Link to="/provider/dashboard/stylists/new" style={quickLinkStyle}>
            Add stylist
          </Link>
          <Link to="/provider/onboarding/review" style={quickLinkStyle}>
            Review step
          </Link>
        </div>

        <div
          style={{
            display: "grid",
            gap: "1.5rem",
            gridTemplateColumns: "280px minmax(0, 1fr)",
            alignItems: "start",
          }}
        >
          <aside
            style={{
              position: "sticky",
              top: "1.5rem",
            }}
          >
            <OnboardingStepper />
          </aside>

          <section
            aria-label="Onboarding content"
            style={{
              background: panelBackground,
              borderRadius: 20,
              border: `1px solid ${borderColor}`,
              padding: "1.5rem",
              minHeight: 460,
              boxShadow:
                "0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 30px rgba(15, 23, 42, 0.06)",
            }}
          >
            {content}
          </section>
        </div>
      </div>
    </div>
  );
};

export default OnboardingShell;